/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Per-browser memory of which share methods already incremented an article's
 * first-party share counter — so repeat clicks don't inflate the visible
 * "N× gedeeld" count. Analytics events (GA4/event-ping) still fire on
 * every click; only the counter ping is suppressed. Mirrors the
 * reviewFlagMemory.ts localStorage pattern: SSR-safe, private-mode-safe.
 */

import type { ShareMethod } from '../types/api/articleShares';

// The methods are ONE definition shared with the server's counter
// (server/data/articleShares.ts); re-exported for the share row.
export type { ShareMethod };

const storageKey = (articleId: string) => `ere_article_shared_${articleId}`;

export function getCountedShareMethods(articleId: string): ShareMethod[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(storageKey(articleId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (m): m is ShareMethod => m === 'copy' || m === 'linkedin' || m === 'whatsapp' || m === 'x'
    );
  } catch {
    return [];
  }
}

/**
 * Record that `method` was counted for this article. Returns true when this
 * was the FIRST time (i.e. the caller should send the counter ping), false
 * when it was already counted or storage is unavailable-but-consistent.
 * When localStorage is unusable (private mode, quota) we still return true —
 * better an occasionally duplicated count than never counting at all.
 */
export function markShareCounted(articleId: string, method: ShareMethod): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const existing = getCountedShareMethods(articleId);
    if (existing.includes(method)) return false;
    window.localStorage.setItem(storageKey(articleId), JSON.stringify([...existing, method]));
    return true;
  } catch {
    return true;
  }
}
